import { Images } from '@/assets/images';
import { DocTypeEnum } from '@/constant';
import { cls, dayjs, formatDate } from '@/utils';

import { FormConfig } from '../define-document-write.type';

const initializeValue = {
	fullName: '',
	dateOfBirth: '',
	gender: '',
	nationality: '',
	expectedEntryDate: '',
	jurisdictionRegion: '',
	passportNumber: '',
	createDate: '',
	employerSignatureName: '',
	employerSignature: '',
	addresseeOfficial: '',
};

type IValues = typeof initializeValue;

export const ACCIDENT_INSURANCE_COMMITMENT_CONFIG: FormConfig<
	IValues,
	DocTypeEnum.ARF_13
> = {
	initializeValue,
	title: '상해보험 가입 확약서',
	mapPropToValues: ({ applicantData, detailData }) => {
		const { unitInfo } = detailData || {};
		return {
			createDate: formatDate(dayjs()),
			fullName: applicantData?.fullName || '',
			dateOfBirth: applicantData?.birthDate || '',
			gender: applicantData?.genderName || '',
			nationality: applicantData?.nationalName || '',
			expectedEntryDate: applicantData?.entryDate || '',
			passportNumber: applicantData?.passportNo || '',
			employerSignatureName: applicantData?.fullName || '',
			addresseeOfficial: unitInfo?.name || '',
			jurisdictionRegion: `${unitInfo?.cityProvince} ${unitInfo?.countyDistrict ? `(${unitInfo?.countyDistrict})` : ''}`,
		};
	},
	pages: [
		{
			background: {
				src: Images.formAccidentInsuranceCommitment,
				className: 'template',
				dataSource: Images.formAccidentInsuranceCommitment,
			},
			textAreas: [
				(values, index) => ({
					name: 'fullName',
					maxHeight: 20,
					className: 'text-[13px] text-left',
					containerClassName: cls(
						'absolute top-[279px] left-[67px] !h-[21px] w-[310px]'
						// values?.fullName && 'bg-white'
					),
					maxLength: 26,
				}),

				(values, index) => ({
					name: 'gender',
					maxHeight: 20,
					className: 'text-[13px] text-left',
					containerClassName: cls(
						'absolute top-[325px] left-[67px] !h-[21px] w-[310px]'
						// values?.gender && 'bg-white'
					),
					maxLength: 60,
				}),
				(values, index) => ({
					name: 'nationality',
					maxHeight: 20,
					className: 'text-[13px] text-left',
					containerClassName: cls(
						'absolute top-[325px] left-[385px] !h-[21px] w-[310px]'
						// values?.nationality && 'bg-white'
					),
					maxLength: 14,
				}),
				(values, index) => ({
					name: 'jurisdictionRegion',
					maxHeight: 20,
					className: 'text-[13px] text-left',
					containerClassName: cls(
						'absolute top-[373px] left-[385px] !h-[21px] w-[310px]'
						// values?.jurisdictionRegion && 'bg-white'
					),
					maxLength: 60,
				}),
				(values, index) => ({
					name: 'passportNumber',
					maxHeight: 20,
					className: 'text-[13px] text-left',
					containerClassName: cls(
						'absolute top-[419px] left-[68px] !h-[21px] w-[625px]'
						// values?.passportNumber && 'bg-white'
					),
					maxLength: 60,
				}),
				{
					name: 'employerSignatureName',
					maxHeight: 40,
					className: 'text-[13px] text-left',
					containerClassName: cls(
						'absolute top-[836px] left-[379px] !h-[40px] w-[150px]'
					),
					maxLength: 20,
				},
				(values, index) => ({
					name: 'addresseeOfficial',
					maxHeight: 40,
					className: 'text-[13px] text-left',
					containerClassName: cls(
						'absolute top-[956px] left-[189px] !h-[21px] w-[86px]',
						values?.addresseeOfficial && 'bg-white'
					),
					maxLength: 16,
				}),
			],
			datePickers: [
				(values, index) => ({
					name: 'dateOfBirth',
					className: '[&>.ant-picker-input>input]:!text-[13px] text-left px-1',
					containerClassName: cls(
						'absolute top-[277px] left-[385px] !h-[21px] w-[310px]'
						// values?.dateOfBirth && 'bg-white'
					),
					style: {
						width: '310px',
						fontSize: '13px',
						marginTop: '-7px',
					},
				}),
				(values, index) => ({
					name: 'expectedEntryDate',
					className: '[&>.ant-picker-input>input]:!text-[13px] text-left px-1',
					containerClassName: cls(
						'absolute top-[371px] left-[67px] !h-[21px] w-[310px]'
						// values?.expectedEntryDate && 'bg-white'
					),
					format: 'YYYY-MM-DD',
					style: {
						width: '310px',
						fontSize: '13px',
						marginTop: '-7px',
					},
				}),
				{
					name: 'createDate',
					segmentedInputs: true,
					format: 'YYYY               MM               DD',
					containerClassName: 'absolute top-[780px] left-[280px]',
					style: { fontSize: '13px' },
					boxStyle: { marginTop: '-11px' },
				},
			],
			signatures: [
				{
					name: 'employerSignature',
					className: 'absolute top-[843px] left-[535px] !h-[48px] w-[115px]',
				},
			],
			highlightBoxes: [
				{
					name: 'fullName',
					className: 'top-[279px] left-[67px] !h-[21px] w-[310px]',
				},
				{
					name: 'dateOfBirth',
					className: 'top-[277px] left-[385px] !h-[21px] w-[310px]',
				},
				{
					name: 'gender',
					className: 'top-[325px] left-[67px] !h-[21px] w-[310px]',
				},
				{
					name: 'nationality',
					className: 'top-[325px] left-[385px] !h-[21px] w-[310px]',
				},
				{
					name: 'expectedEntryDate',
					className: 'top-[371px] left-[67px] !h-[21px] w-[310px]',
				},
				{
					name: 'jurisdictionRegion',
					className: 'top-[373px] left-[385px] !h-[21px] w-[310px]',
				},
				{
					name: 'passportNumber',
					className: 'top-[419px] left-[68px] !h-[21px] w-[625px]',
				},
				{
					name: 'createDate',
					className: 'top-[770px] left-[280px] !h-[21px] w-[192px]',
				},
				{
					name: 'employerSignatureName',
					className: 'top-[843px] left-[113px] !h-[21px] w-[265px]',
				},
				{
					name: 'employerSignature',
					className: 'top-[843px] left-[535px] !h-[21px] w-[115px]',
				},
				{
					name: 'addresseeOfficial',
					className: 'top-[956px] left-[189px] !h-[21px] w-[86px]',
				},
			],
		},
	],
};
